import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileCog, ClipboardList, Settings, Archive, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { reportingApi } from '../services/reportingApi';

const Home = () => {
  const [lastRun, setLastRun] = useState(null);
  const [refReady, setRefReady] = useState(null);
  const [connectorReady, setConnectorReady] = useState(null);
  const [runsLoaded, setRunsLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      reportingApi.getRuns(1).catch(() => ({ data: { items: [] } })),
      reportingApi.getReferenceCounts().catch(() => ({ data: { sm_poste_count: 0 } })),
      reportingApi.getSettings().catch(() => ({ data: {} })),
    ]).then(([runsRes, countsRes, settingsRes]) => {
      const runs = runsRes.data?.items || [];
      setLastRun(runs[0] || null);
      setRefReady((countsRes.data?.sm_poste_count || 0) > 0);
      setConnectorReady(!!settingsRes.data?.db_connector_id);
      setRunsLoaded(true);
    });
  }, []);

  const isConfigured = refReady && connectorReady;

  const workflow = [
    {
      step: '1',
      title: 'Paramétrages',
      desc: 'Connecter Amplitude et charger les tables COBAC de référence.',
      href: '/settings',
      icon: Settings,
      done: isConfigured,
    },
    {
      step: '2',
      title: 'Produire un reporting',
      desc: "Sélectionner la date d'arrêté et lancer le chargement depuis Amplitude.",
      href: '/reporting/produce',
      icon: FileCog,
      done: lastRun?.status === 'completed',
    },
    {
      step: '3',
      title: 'Consulter et valider',
      desc: 'Parcourir les déclarations COBAC, vérifier les montants, générer le fichier TXT.',
      href: '/reporting/consult',
      icon: ClipboardList,
      done: false,
    },
    {
      step: '4',
      title: 'Archiver',
      desc: 'Exporter et supprimer les données des arrêtés clôturés.',
      href: '/archives',
      icon: Archive,
      done: false,
    },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Tableau de bord</h1>
        <p className="text-sm text-slate-500 mt-1">Reporting réglementaire COBAC — SM_Report</p>
      </div>

      {/* Config warning */}
      {runsLoaded && !isConfigured && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-300 text-amber-800 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            L&apos;application n&apos;est pas encore configurée.{' '}
            {!connectorReady && "Le connecteur Amplitude n'est pas défini. "}
            {!refReady && "Les tables de référence COBAC ne sont pas chargées. "}
            <Link to="/settings" className="font-medium underline">Aller dans les Paramétrages</Link>.
          </span>
        </div>
      )}

      {/* Dernière production */}
      {runsLoaded && (
        <div className="bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-slate-800 mb-4">Dernière production</h2>
          {lastRun ? (
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="space-y-1">
                <p className="text-lg font-bold text-slate-900">Arrêté au {lastRun.closing_date}</p>
                <p className="text-sm text-slate-500">
                  {lastRun.fichier ? `Fichier ${lastRun.fichier}` : 'Tous les fichiers'} — {lastRun.rows_loaded ?? 0} postes chargés
                </p>
                <p className="text-xs text-slate-400">
                  Lancé par {lastRun.started_by || '—'} le {lastRun.started_at ? new Date(lastRun.started_at).toLocaleString('fr-FR') : '—'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {lastRun.status === 'completed'
                  ? <><CheckCircle2 className="h-5 w-5 text-green-600" /><span className="text-sm font-medium text-green-700">Terminé</span></>
                  : lastRun.status === 'failed'
                  ? <><AlertTriangle className="h-5 w-5 text-red-600" /><span className="text-sm font-medium text-red-700">Échec</span></>
                  : <><Clock className="h-5 w-5 text-yellow-600" /><span className="text-sm font-medium text-yellow-700">En cours</span></>
                }
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Aucune production effectuée.{' '}
              <Link to="/reporting/produce" className="text-blue-800 underline font-medium">Lancer la première production</Link>.
            </p>
          )}
        </div>
      )}

      {/* Workflow guidé */}
      <div>
        <h2 className="text-base font-semibold text-slate-800 mb-4">Workflow</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {workflow.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.step}
                to={item.href}
                className="bg-white border border-slate-200 shadow-sm p-5 space-y-3 hover:border-[#E20613]/40 hover:shadow-md transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#E20613] text-white text-xs font-bold">
                    {item.step}
                  </div>
                  {item.done && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                </div>
                <Icon className="h-6 w-6 text-[#004A99]" />
                <div>
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-[#004A99]">{item.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default Home;
